import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { overlayDismiss } from '../utils/modalDismiss';
import {
  STATUS_BADGES, TRAINING_BADGES, formatWindow, formatDay, toDateTimeLocal, toDateInput, buildDays
} from '../utils/attendance';

// Today, 09:00 to 17:30 local time -- a typical training day.
const defaultWindow = () => {
  const start = new Date();
  start.setHours(9, 0, 0, 0);
  const end = new Date(start);
  end.setHours(17, 30, 0, 0);
  return { starts_at: toDateTimeLocal(start), ends_at: toDateTimeLocal(end) };
};

const newTrainingForm = () => ({
  title: '', category_id: '', createDays: true, count: 5,
  firstDate: toDateInput(new Date()), startTime: '09:45', endTime: '10:30', skipWeekends: true
});

const TrainingCard = ({ t, onOpen }) => {
  const badge = TRAINING_BADGES[t.status];
  return (
    <div className="card training-card" onClick={onOpen}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="flex flex-center gap-2" style={{ marginBottom: '4px' }}>
          <strong className="truncate" style={{ fontSize: '16px' }}>{t.title}</strong>
          <span className={`badge ${badge.className}`} style={{ flexShrink: 0 }}>
            {t.status === 'open' && t.open_day_title ? `${t.open_day_title} open now` : badge.label}
          </span>
        </div>
        <div className="text-muted text-sm">
          {t.day_count > 0
            ? `${formatDay(t.first_start)} – ${formatDay(t.last_end)} · ${t.day_count} day${t.day_count === 1 ? '' : 's'}`
            : 'No days yet'}
          {` · ${t.trainee_count} trainee${t.trainee_count === 1 ? '' : 's'}`}
          {t.category_name && ` · ${t.category_name}`}
        </div>
      </div>
      <span className="text-muted" style={{ fontSize: '20px' }}>›</span>
    </div>
  );
};

const AttendanceList = () => {
  const [trainings, setTrainings] = useState([]);
  const [singles, setSingles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCompleted, setShowCompleted] = useState(false);
  const [trainingForm, setTrainingForm] = useState(null);
  const [dayForm, setDayForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get('/attendance/trainings'), api.get('/attendance'), api.get('/categories')])
      .then(([t, s, c]) => {
        setTrainings(t.data);
        setSingles(s.data.filter(d => !d.training_id));
        setCategories(c.data);
      })
      .catch(() => setError('Failed to load attendance'))
      .finally(() => setLoading(false));
  }, []);

  const preview = trainingForm?.createDays && trainingForm.firstDate && trainingForm.count > 0
    ? buildDays({ ...trainingForm, count: Math.min(Number(trainingForm.count) || 0, 31) })
    : [];

  const createTraining = async (e) => {
    e.preventDefault();
    if (trainingForm.createDays && trainingForm.endTime <= trainingForm.startTime) {
      alert('The scan window must end after it starts');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/attendance/trainings', {
        title: trainingForm.title,
        category_id: trainingForm.category_id || null,
        days: preview.map(({ title, starts_at, ends_at }) => ({ title, starts_at, ends_at }))
      });
      navigate(`/attendance/trainings/${data.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create training');
      setSaving(false);
    }
  };

  const createSingle = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post('/attendance', {
        title: dayForm.title,
        category_id: dayForm.category_id || null,
        starts_at: new Date(dayForm.starts_at).toISOString(),
        ends_at: new Date(dayForm.ends_at).toISOString()
      });
      navigate(`/attendance/${data.id}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create attendance');
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center mt-5">Loading...</div>;

  const active = trainings.filter(t => t.status !== 'completed');
  const completed = trainings.filter(t => t.status === 'completed');
  const setT = (patch) => setTrainingForm({ ...trainingForm, ...patch });

  return (
    <div>
      <div className="flex flex-between flex-center mb-5">
        <div>
          <h1>Attendance</h1>
          <p className="text-muted">Trainees scan a QR code to mark themselves present for each training day</p>
        </div>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={() => setDayForm({ title: '', category_id: '', ...defaultWindow() })}>
            + Single day
          </button>
          <button className="btn btn-primary" onClick={() => setTrainingForm(newTrainingForm())}>+ New Training</button>
        </div>
      </div>

      {error && <div className="alert alert-error mb-4">{error}</div>}

      {/* Trainings */}
      {active.length === 0 && completed.length === 0 ? (
        <div className="card empty-state mb-4">
          <h3>No trainings yet</h3>
          <p>Create a training and its days, then show each day's QR code to the room</p>
          <button className="btn btn-primary mt-3" onClick={() => setTrainingForm(newTrainingForm())}>Create Training</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '12px' }} className="mb-4">
          {active.map(t => <TrainingCard key={t.id} t={t} onOpen={() => navigate(`/attendance/trainings/${t.id}`)} />)}
          {active.length === 0 && <p className="text-muted">No ongoing or upcoming trainings.</p>}
        </div>
      )}

      {completed.length > 0 && (
        <div className="mb-5">
          <button className="btn btn-secondary btn-sm" onClick={() => setShowCompleted(!showCompleted)}>
            {showCompleted ? '▾' : '▸'} Completed trainings ({completed.length})
          </button>
          {showCompleted && (
            <div style={{ display: 'grid', gap: '12px', marginTop: '12px' }}>
              {completed.map(t => <TrainingCard key={t.id} t={t} onOpen={() => navigate(`/attendance/trainings/${t.id}`)} />)}
            </div>
          )}
        </div>
      )}

      {/* Days that do not belong to a training */}
      {singles.length > 0 && (
        <>
          <h3 className="mb-3">Other attendance</h3>
          <div className="card" style={{ padding: 0 }}>
            <table className="table">
              <thead>
                <tr><th>Title</th><th>Category</th><th>Window</th><th>Status</th><th>Present</th></tr>
              </thead>
              <tbody>
                {singles.map(s => (
                  <tr key={s.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/attendance/${s.id}`)}>
                    <td><strong>{s.title}</strong></td>
                    <td className="text-muted">{s.category_name || '—'}</td>
                    <td className="text-sm">{formatWindow(s.starts_at, s.ends_at)}</td>
                    <td><span className={`badge ${STATUS_BADGES[s.status].className}`}>{STATUS_BADGES[s.status].label}</span></td>
                    <td>{s.present_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* New training */}
      {trainingForm && (
        <div className="modal-overlay" {...overlayDismiss(() => setTrainingForm(null))}>
          <div className="modal">
            <div className="modal-header">
              <h3>New Training</h3>
              <button className="modal-close" onClick={() => setTrainingForm(null)}>&times;</button>
            </div>
            <form onSubmit={createTraining}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Training name</label>
                  <input type="text" required maxLength={255} autoFocus
                    placeholder="e.g. Certified Pharma Investigator - Oct 2026 batch"
                    value={trainingForm.title} onChange={e => setT({ title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Category (optional)</label>
                  <select value={trainingForm.category_id} onChange={e => setT({ category_id: e.target.value })}>
                    <option value="">— None —</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <label className="flex flex-center gap-2 mb-3" style={{ cursor: 'pointer' }}>
                  <input type="checkbox" checked={trainingForm.createDays}
                    onChange={e => setT({ createDays: e.target.checked })} />
                  Create the training days now
                </label>
                {trainingForm.createDays && (
                  <>
                    <div className="grid grid-2">
                      <div className="form-group">
                        <label>Number of days</label>
                        <input type="number" min={1} max={31} required value={trainingForm.count}
                          onChange={e => setT({ count: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>First day</label>
                        <input type="date" required value={trainingForm.firstDate}
                          onChange={e => setT({ firstDate: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>Scan opens (each day)</label>
                        <input type="time" required value={trainingForm.startTime}
                          onChange={e => setT({ startTime: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>Scan closes (each day)</label>
                        <input type="time" required value={trainingForm.endTime}
                          onChange={e => setT({ endTime: e.target.value })} />
                      </div>
                    </div>
                    <label className="flex flex-center gap-2 mb-3" style={{ cursor: 'pointer' }}>
                      <input type="checkbox" checked={trainingForm.skipWeekends}
                        onChange={e => setT({ skipWeekends: e.target.checked })} />
                      Skip Saturdays and Sundays
                    </label>
                    {preview.length > 0 && (
                      <p className="text-muted text-sm">
                        {preview.map(d => `${d.title}: ${formatDay(d.starts_at)}`).join(' · ')}
                      </p>
                    )}
                  </>
                )}
                <p className="text-muted text-sm mt-2">You can add, edit or remove days afterwards.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setTrainingForm(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Single day */}
      {dayForm && (
        <div className="modal-overlay" {...overlayDismiss(() => setDayForm(null))}>
          <div className="modal">
            <div className="modal-header">
              <h3>Single-Day Attendance</h3>
              <button className="modal-close" onClick={() => setDayForm(null)}>&times;</button>
            </div>
            <form onSubmit={createSingle}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" required maxLength={255} autoFocus
                    placeholder="e.g. GMP refresher"
                    value={dayForm.title} onChange={e => setDayForm({ ...dayForm, title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Category (optional)</label>
                  <select value={dayForm.category_id} onChange={e => setDayForm({ ...dayForm, category_id: e.target.value })}>
                    <option value="">— None —</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-2">
                  <div className="form-group">
                    <label>Opens</label>
                    <input type="datetime-local" required value={dayForm.starts_at}
                      onChange={e => setDayForm({ ...dayForm, starts_at: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Closes</label>
                    <input type="datetime-local" required value={dayForm.ends_at}
                      onChange={e => setDayForm({ ...dayForm, ends_at: e.target.value })} />
                  </div>
                </div>
                <p className="text-muted text-sm">For a one-off session. Use "New Training" for courses that run over several days.</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setDayForm(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceList;
